import "./components/message-bubble";
import "./components/recent-chats";
import "./components/typing-indicator";
import "./components/message-list";
import "./components/composer";
import "./components/overflow-menu";
import "./components/suggestions";
import {
  ChatGrowWidget,
  type ChatgrowTriggerSource,
} from "./widget/chatgrow-widget";
import "@shoelace-style/shoelace/dist/themes/light.css";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/icon-button/icon-button.js";

import { registerIconLibrary } from "@shoelace-style/shoelace/dist/utilities/icon-library.js";

registerIconLibrary("default", {
  resolver: (name) =>
    `https://cdn.jsdelivr.net/npm/@shoelace-style/shoelace/dist/assets/icons/${name}.svg`,
});

if (!customElements.get("chatgrow-widget")) {
  customElements.define("chatgrow-widget", ChatGrowWidget);
}

// Bootloader
interface ChatGrowConfig {
  agentId: string;
  apiBase?: string;
  defaultOpen?: boolean;
  position?: "bottom-right" | "bottom-left";
  theme?: "auto" | "light" | "dark";
  suggestions?: string[];
  initialMessages?: string[];
  salutations?: string[];
  showRecentChats?: boolean;
  showBranding?: boolean;
}

type ChatgrowCommand = "open" | "close" | "toggle";

interface ChatgrowCommandFunction {
  (command: ChatgrowCommand, ...args: unknown[]): void;
  q?: ArrayLike<unknown>[];
}

declare global {
  interface Window {
    chatgrowConfig?: ChatGrowConfig;
    Chatgrow?: ChatgrowCommandFunction;
  }
}

const pendingCommands: Array<{
  command: ChatgrowCommand;
  source: ChatgrowTriggerSource;
}> = [];

function getWidget(): ChatGrowWidget | null {
  return document.querySelector<ChatGrowWidget>("chatgrow-widget");
}

function runCommand(
  command: ChatgrowCommand,
  source: ChatgrowTriggerSource,
): boolean {
  const widget = getWidget();
  if (!widget) {
    pendingCommands.push({ command, source });
    return false;
  }

  if (command === "open") return widget.open(source);
  if (command === "close") return widget.close();
  return widget.toggle(source);
}

function flushPendingCommands(): void {
  if (!getWidget()) return;

  const commands = pendingCommands.splice(0);
  for (const { command, source } of commands) {
    runCommand(command, source);
  }
}

function isChatgrowCommand(value: unknown): value is ChatgrowCommand {
  return value === "open" || value === "close" || value === "toggle";
}

function installPublicApi(): void {
  const queuedCalls = window.Chatgrow?.q
    ? Array.from(window.Chatgrow.q)
    : [];

  window.Chatgrow = (command: ChatgrowCommand) => {
    if (!isChatgrowCommand(command)) {
      console.warn(
        `[ChatGrow] Unknown command "${String(command)}". Use open, close, or toggle.`,
      );
      return;
    }
    runCommand(command, "api");
  };

  for (const queuedCall of queuedCalls) {
    const [command] = Array.from(queuedCall);
    if (isChatgrowCommand(command)) runCommand(command, "api");
  }

  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest(
      "[data-chatgrow-open], [data-chatgrow-close], [data-chatgrow-toggle]",
    );
    if (!trigger) return;

    if (trigger.hasAttribute("data-chatgrow-open")) {
      runCommand("open", "attribute");
    } else if (trigger.hasAttribute("data-chatgrow-close")) {
      runCommand("close", "attribute");
    } else {
      runCommand("toggle", "attribute");
    }
  });
}

function init() {
  if (typeof window === "undefined") return;

  // Check for existing widget to avoid duplicates
  if (getWidget()) {
    flushPendingCommands();
    return;
  }

  const config = window.chatgrowConfig;
  if (!config?.agentId) return;

  const widget = document.createElement("chatgrow-widget");
  widget.setAttribute("agent-id", config.agentId);

  if (config.apiBase) widget.setAttribute("api-base", config.apiBase);
  if (config.defaultOpen !== undefined)
    widget.setAttribute("default-open", String(config.defaultOpen));
  if (config.position) widget.setAttribute("position", config.position);
  if (config.theme) widget.setAttribute("theme", config.theme);
  if (config.suggestions?.length)
    widget.setAttribute("suggestions", config.suggestions.join(","));
  if (config.initialMessages?.length)
    widget.setAttribute("initial-messages", config.initialMessages.join(","));
  if (config.salutations?.length)
    widget.setAttribute("salutations", JSON.stringify(config.salutations));
  if (config.showRecentChats !== undefined)
    widget.setAttribute("show-recent-chats", String(config.showRecentChats));
  if (config.showBranding !== undefined)
    widget.setAttribute("show-branding", String(config.showBranding));

  document.body.appendChild(widget);
  flushPendingCommands();
}

installPublicApi();

// Run init when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
